import { useMemo, useRef } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { SearchIcon, UserIcon, XCircleIcon } from 'lucide-react';

import { api } from '@/lib/api';
import { groupByCandidate } from '@/lib/candidates';
import { useLabel, useT, useTn } from '@/lib/i18n';
import { useUrlState } from '@/lib/url-state';
import { useSearchShortcut } from '@/lib/shortcuts';
import { SavedFilters } from '@/components/saved-filters';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useModuleName } from '@/hooks/use-module-name';
import { usePersonaName } from '@/hooks/use-persona-name';

/**
 * Candidates — assessment instances by candidateRef, read from the pipeline.
 *
 * This is the first screen that shows Phase 3 actually running. It reads the
 * instance feed (`GET /api/assessments`) rather than the results feed, and that
 * choice is the point: a results-only view cannot show anyone mid-assessment,
 * because `ready` and `in_progress` runs have no result yet. A delivery screen
 * that only lists finished work is not a delivery screen.
 *
 * NOT the Phase-5/6 build (`09`): no DataTable (that shared component is Phase
 * 5's to define — building a one-off table here would either duplicate it or
 * pre-empt its API), no drill-down into a run, no take-test harness. Those need
 * screens that do not exist yet.
 *
 * NO PII, ever. `candidateRef` is an opaque identifier and is the only thing
 * about a person that this platform stores or displays.
 */

const STATE_TONE: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  completed: 'default',
  in_progress: 'secondary',
  ready: 'outline',
  submitted: 'secondary',
  scoring: 'secondary',
  expired: 'destructive',
  flagged: 'destructive',
};

/**
 * The grouping moved to `lib/candidates.ts` when the topbar search (X2) needed
 * the same roster. There is no candidates endpoint, so "who exists" is a
 * derivation — and a derivation with two implementations is two answers.
 */

export function Candidates() {
  const t = useT();
  const moduleName = useModuleName();
  const personaName = usePersonaName();
  const tn = useTn();
  const label = useLabel();
  // In the URL, so a search for one candidate reference is a link.
  const [{ q: search }, setFilters] = useUrlState({ q: '' });
  const searchBox = useRef<HTMLInputElement>(null);
  useSearchShortcut(searchBox);

  const instances = useQuery({
    queryKey: ['assessments'],
    queryFn: () => api.assessments({ pageSize: 100 }),
    refetchInterval: 30_000,
  });
  const results = useQuery({
    queryKey: ['results'],
    queryFn: () => api.results({ pageSize: 100 }),
    refetchInterval: 30_000,
  });

  const isLoading = instances.isLoading || results.isLoading;
  const error = instances.error ?? results.error;

  const rows = useMemo(
    () => groupByCandidate(instances.data?.items ?? [], results.data?.items ?? []),
    [instances.data, results.data],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.candidateRef.toLowerCase().includes(q) ||
        r.instances.some((i) => i.moduleId.toLowerCase().includes(q) || i.personaId.toLowerCase().includes(q)),
    );
  }, [rows, search]);

  const live = rows.filter((r) => r.instances.some((i) => i.state === 'in_progress' || i.state === 'ready')).length;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={t('nav.candidates')}
        description={t('candidates.description')}
        actions={
          instances.data && (
            <>
              <Badge variant="secondary">{tn('candidates.count', rows.length)}</Badge>
              <Badge variant={live ? 'default' : 'outline'}>
                {t('candidates.inFlight', { count: live })}
              </Badge>
            </>
          )
        }
      />

      {isLoading && (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      )}

      {error && (
        <EmptyState
          icon={XCircleIcon}
          title={t('scoring.unreachable')}
          description={error instanceof Error ? error.message : t('candidates.feedSilent')}
        />
      )}

      {!isLoading && !error && rows.length === 0 && (
        <EmptyState
          icon={UserIcon}
          title={t('candidates.empty.title')}
          description={t('candidates.empty.description')}
        />
      )}

      {!isLoading && !error && rows.length > 0 && (
        <>
          <div className="mb-3 flex justify-end">
            <SavedFilters />
          </div>

          <div className="relative mb-4">
            <SearchIcon className="text-muted-foreground pointer-events-none absolute inset-inline-start-3 top-1/2 size-4 -translate-y-1/2" />
            <Input
              ref={searchBox}
              value={search}
              onChange={(e) => setFilters({ q: e.target.value })}
              placeholder={t('candidates.searchPlaceholder')}
              aria-label={t('candidates.searchPlaceholder')}
              className="ps-9"
            />
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              icon={SearchIcon}
              title={t('candidates.noMatches')}
              description={t('candidates.noMatches.description', { query: search })}
              actions={
                <Button variant="outline" onClick={() => setFilters({ q: '' })}>
                  {t('questions.empty.clearFilters')}
                </Button>
              }
            />
          ) : (
            <div className="space-y-4">
              {filtered.map((row) => (
                <Card key={row.candidateRef}>
                  <CardHeader className="pb-3">
                    {/* W1: the reference is now the way in. This card was
                        terminal — the whole screen could tell you a candidate
                        existed and never what they were supposed to be sitting. */}
                    <CardTitle className="text-base">
                      <Link
                        to={`/candidates/${encodeURIComponent(row.candidateRef)}`}
                        className="hover:text-primary font-mono underline-offset-4 hover:underline"
                        dir="ltr"
                      >
                        {row.candidateRef}
                      </Link>
                    </CardTitle>
                    <CardDescription>
                      {tn('candidates.instances', row.instances.length)} ·{' '}
                      {tn('candidates.results', row.results.length)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {row.instances.map((i) => {
                      const result = row.results.find((r) => r.instanceId === i.assessmentId);
                      return (
                        <div
                          key={i.assessmentId}
                          className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t pt-2 text-sm first:border-t-0 first:pt-0"
                        >
                          <span className="font-medium">{moduleName(i.moduleId)}</span>
                          <Badge variant={STATE_TONE[i.state] ?? 'outline'}>
                            {label('state', i.state)}
                          </Badge>
                          <span className="text-muted-foreground">
                            {t('candidates.answered', {
                              answered: i.answeredCount,
                              total: i.itemCount,
                              progress: i.progress,
                            })}
                          </span>
                          <span className="text-muted-foreground text-xs">{personaName(i.personaId)}</span>
                          <span className="text-muted-foreground font-mono text-xs">
                            {t('modules.configVersion', { version: i.scoringConfigVersion })}
                          </span>
                          {result?.qcFlags.map((flag) => (
                            <Badge key={flag} variant="destructive">
                              {label('qcFlag', flag)}
                            </Badge>
                          ))}
                          {/**
                            * X1: this was `href={/api/results/…}` opening in a
                            * new tab — the raw API, as a plain navigation with
                            * no `Authorization` header. `nginx.conf` proxies
                            * `/api/` same-origin so it did reach the server,
                            * which answered **401 UNAUTHENTICATED** as JSON.
                            * The only link out of this screen went to an error
                            * envelope.
                            *
                            * It is a router link to the result view now, which
                            * is what `Results.tsx` has always done from its own
                            * rows.
                            */}
                          {result && (
                            <Link
                              className="text-primary ms-auto text-xs underline underline-offset-4"
                              to={`/results/${result.resultId}`}
                            >
                              {t('candidates.viewResult')}
                            </Link>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}


        </>
      )}
    </div>
  );
}
